import { App, Button, Checkbox, InputNumber, Popover, Progress, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analyzePasswordStrength, generatePassword } from '../../../utils/passwordStrength';
import { Icon } from '../../Core/Icon';

interface Props {
  value: string;
  visible: boolean;
  readOnly?: boolean;
  onToggleVisibility: () => void;
  onGenerate?: (password: string) => void;
  inline?: boolean;
  strengthOnly?: boolean;
}

export const PasswordFeatures: React.FC<Props> = ({
  value,
  visible,
  readOnly = false,
  onToggleVisibility,
  onGenerate,
  inline = false,
  strengthOnly = false,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [passwordLength, setPasswordLength] = useState(20);
  const [includeSymbols, setIncludeSymbols] = useState(true);

  const strength = useMemo(() => analyzePasswordStrength(value), [value]);

  const handleGenerate = () => {
    const newPassword = generatePassword({ length: passwordLength, includeSymbols });
    onGenerate?.(newPassword);
    setGeneratorOpen(false);
  };

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      message.success(t('secret.password.copied'));
    } catch {
      message.error('Failed to copy');
    }
  };

  const strengthPercent = ((strength.score + 1) / 5) * 100;

  // Show only the strength indicator
  if (strengthOnly) {
    return (
      <div className="flex items-center gap-2">
        <Progress
          percent={strengthPercent}
          showInfo={false}
          strokeColor={strength.color}
          trailColor="#e8e8e8"
          size="small"
          className="m-0 flex-1"
        />
        <span className="text-xs whitespace-nowrap" style={{ color: strength.color }}>
          {t(`secret.password.strength.${strength.label}`)}
        </span>
      </div>
    );
  }

  // Show only the action buttons (inline mode)
  if (inline) {
    return (
      <div className="flex items-center">
        <Tooltip title={visible ? t('secret.password.hide') : t('secret.password.show')}>
          <Button
            type="text"
            size="small"
            aria-label={visible ? t('secret.password.hide') : t('secret.password.show')}
            className="text-gray-dark hover:text-azure flex items-center px-1.5"
            icon={<Icon name={visible ? 'EyeOffIcon' : 'EyeIcon'} className="h-4 w-4" />}
            onClick={onToggleVisibility}
          />
        </Tooltip>
        <Tooltip title={t('secret.password.copy')}>
          <Button
            type="text"
            size="small"
            aria-label={t('secret.password.copy')}
            className="text-gray-dark hover:text-azure flex items-center px-1.5"
            icon={<Icon name="CopyIcon" className="h-4 w-4" />}
            onClick={handleCopy}
          />
        </Tooltip>
        {!readOnly && onGenerate && (
          <Popover
            open={generatorOpen}
            onOpenChange={setGeneratorOpen}
            trigger="click"
            placement="bottomRight"
            content={
              <div className="flex flex-col gap-3 p-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{t('secret.password.length')}:</span>
                  <InputNumber
                    size="small"
                    min={8}
                    max={64}
                    value={passwordLength}
                    onChange={(v) => setPasswordLength(v || 20)}
                    className="w-20"
                  />
                </div>
                <Checkbox
                  checked={includeSymbols}
                  onChange={(e) => setIncludeSymbols(e.target.checked)}
                >
                  {t('secret.password.includeSymbols')}
                </Checkbox>
                <Button type="primary" size="small" onClick={handleGenerate}>
                  {t('secret.password.generate')}
                </Button>
              </div>
            }
          >
            <Tooltip title={t('secret.password.generate')}>
              <Button
                type="text"
                size="small"
                aria-label={t('secret.password.generate')}
                className="text-gray-dark hover:text-azure flex items-center px-1.5"
                icon={<Icon name="RotateCwIcon" className="h-4 w-4" />}
              />
            </Tooltip>
          </Popover>
        )}
      </div>
    );
  }

  // Default: show both buttons and strength
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <Tooltip title={visible ? t('secret.password.hide') : t('secret.password.show')}>
          <Button
            type="text"
            size="small"
            aria-label={visible ? t('secret.password.hide') : t('secret.password.show')}
            icon={<Icon name={visible ? 'EyeOffIcon' : 'EyeIcon'} className="h-4 w-4" />}
            onClick={onToggleVisibility}
          />
        </Tooltip>
        {!readOnly && onGenerate && (
          <Popover
            open={generatorOpen}
            onOpenChange={setGeneratorOpen}
            trigger="click"
            placement="bottom"
            content={
              <div className="flex flex-col gap-3 p-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{t('secret.password.length')}:</span>
                  <InputNumber
                    size="small"
                    min={8}
                    max={64}
                    value={passwordLength}
                    onChange={(v) => setPasswordLength(v || 20)}
                    className="w-20"
                  />
                </div>
                <Checkbox
                  checked={includeSymbols}
                  onChange={(e) => setIncludeSymbols(e.target.checked)}
                >
                  {t('secret.password.includeSymbols')}
                </Checkbox>
                <Button type="primary" size="small" onClick={handleGenerate}>
                  {t('secret.password.generate')}
                </Button>
              </div>
            }
          >
            <Tooltip title={t('secret.password.generate')}>
              <Button
                type="text"
                size="small"
                aria-label={t('secret.password.generate')}
                icon={<Icon name="RotateCwIcon" className="h-4 w-4" />}
              />
            </Tooltip>
          </Popover>
        )}
      </div>
      {value && (
        <div className="flex items-center gap-2">
          <Progress
            percent={strengthPercent}
            showInfo={false}
            strokeColor={strength.color}
            trailColor="#e8e8e8"
            size="small"
            className="m-0 flex-1"
          />
          <span className="text-xs whitespace-nowrap" style={{ color: strength.color }}>
            {t(`secret.password.strength.${strength.label}`)}
          </span>
        </div>
      )}
    </div>
  );
};

export default PasswordFeatures;
